import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import {
  Target,
  Building2,
  Award,
  ArrowUpRight,
  Loader2,
  AlertCircle,
  Pencil,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { isAuthError } from "@/services/api/client";
import { perfilamientoService } from "@/services/perfilamiento.service";
import { companyProfileService } from "@/services/companyProfile.service";
import { mockCompanyProfile } from "@/lib/intelligence/company-profile.mock";
import { computeProfileCompleteness } from "@/lib/intelligence/company-profile-completeness";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { PatronCompraPanel } from "@/components/oc/PatronCompraPanel";

export const Route = createFileRoute("/perfilamiento")({
  head: () => ({ meta: [{ title: "Perfilamiento Estratégico — LicitaPro" }] }),
  component: Page,
});

function Page() {
  const profileQuery = useQuery({
    queryKey: ["company-profile"],
    queryFn: () => companyProfileService.get(),
  });

  const compatQuery = useQuery({
    queryKey: ["perfilamiento", "compatibilidad"],
    queryFn: () => perfilamientoService.compatibilidad(),
  });

  const benchQuery = useQuery({
    queryKey: ["perfilamiento", "benchmark"],
    queryFn: () => perfilamientoService.benchmark(),
  });

  const profile = profileQuery.data ?? mockCompanyProfile;
  const compat = compatQuery.data ?? [];
  const benchmark = benchQuery.data ?? [];

  const isLoading = profileQuery.isLoading || compatQuery.isLoading || benchQuery.isLoading;

  const sessionExpired = [profileQuery.error, compatQuery.error, benchQuery.error].some((e) =>
    isAuthError(e),
  );

  // El tenant aún no completó su ficha de empresa: es un dato ausente (`null`),
  // no un error. No mostramos datos de demostración como si fueran reales.
  const profileMissing = !profileQuery.isLoading && profileQuery.data === null;
  // Solo se calcula sobre datos reales: si no hay perfil, `profile` cae al mock
  // completo y daría un falso 100% de completitud.
  const completeness = profileQuery.data ? computeProfileCompleteness(profileQuery.data) : null;


  const globalScore = compat.length
    ? Math.round(compat.reduce((a, c) => a + c.score, 0) / compat.length)
    : 0;

  const scoreLabel =
    globalScore >= 80 ? "Excelente posicionamiento competitivo" :
    globalScore >= 60 ? "Buen posicionamiento competitivo" :
    globalScore >= 40 ? "Posicionamiento regular" :
    "Posicionamiento débil";

  return (
    <AppShell
      title="Perfilamiento Estratégico"
      subtitle="Tu posición competitiva en el mercado público"
      actions={
        <Button asChild size="sm" variant="outline">
          <Link to="/empresa">
            <Pencil className="size-3.5 mr-1.5" /> Editar perfil
          </Link>
        </Button>
      }
    >
      {sessionExpired ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center shadow-elegant">
          <AlertCircle className="size-8 text-warning mx-auto mb-3" />
          <h2 className="font-display text-lg font-semibold text-foreground">
            Tu sesión expiró
          </h2>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            No pudimos validar tu sesión con el servidor. Vuelve a ingresar para ver tu
            perfilamiento con datos reales.
          </p>
          <Button size="sm" onClick={() => window.location.reload()}>
            Reintentar sesión
          </Button>
        </div>
      ) : isLoading ? (

        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
            {/* Perfil empresa */}
            <div className="xl:col-span-2 rounded-xl border border-border bg-card p-6 shadow-elegant">
              {profileMissing ? (
                <div className="flex flex-col items-center justify-center text-center py-6">
                  <Building2 className="size-8 text-muted-foreground mb-3 opacity-50" />
                  <h2 className="font-display text-lg font-semibold text-foreground">
                    Completa el perfil de tu empresa
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1 mb-3 max-w-sm">
                    Configura rubros, presupuesto y preferencias para ver tu compatibilidad por
                    categoría y comparativas reales.
                  </p>
                  <div className="w-full max-w-xs mb-4">
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                      <span>Completitud del perfil</span>
                      <span className="font-mono tabular-nums">0/6</span>
                    </div>
                    <Progress value={0} className="h-1.5" />
                  </div>
                  <Button asChild size="sm">
                    <Link to="/empresa">
                      <Pencil className="size-3.5 mr-1.5" /> Completar perfil
                    </Link>
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="size-16 rounded-xl bg-gradient-primary grid place-items-center shadow-glow shrink-0">
                      <Building2 className="size-7 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="font-display text-xl font-semibold text-foreground">
                          {profile.datos.razonSocial}
                        </h2>
                        {completeness && !completeness.isComplete && (
                          <Link
                            to="/empresa"
                            className="text-[11px] text-warning font-mono tabular-nums shrink-0 hover:underline"
                          >
                            {completeness.completed}/{completeness.total} · completar
                          </Link>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        RUT {profile.datos.rut} · {profile.datos.tamano} empresa ·{" "}
                        {profile.datos.aniosExperiencia} años de experiencia
                      </p>
                      {completeness && !completeness.isComplete && (
                        <Progress value={completeness.percent} className="h-1 mt-2 max-w-[220px]" />
                      )}
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {profile.datos.rubros.slice(0, 6).map((t) => (
                          <span
                            key={t}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                    <div>
                      <div className="text-xs text-muted-foreground">Experiencia</div>
                      <div className="font-display text-lg font-semibold text-foreground">
                        {profile.datos.aniosExperiencia} años
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Contratos ejecutados</div>
                      <div className="font-display text-lg font-semibold text-foreground">
                        {profile.historial.ganadas + profile.historial.perdidas}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Score performance</div>
                      <div className="font-display text-lg font-semibold text-success">
                        {globalScore >= 80 ? "A+" : globalScore >= 60 ? "A" : globalScore >= 40 ? "B" : "C"}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Score compatibilidad */}
            <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-card p-6 shadow-glow">
              <div className="flex items-center gap-2 mb-3">
                <Target className="size-4 text-primary" />
                <span className="text-sm font-medium text-foreground">Match Score Global</span>
              </div>
              <div className="font-display text-5xl font-semibold text-foreground tabular-nums">
                {globalScore}
                <span className="text-2xl text-muted-foreground">/100</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{scoreLabel}</div>
              <div className="mt-4 space-y-2">
                {compat.slice(0, 4).map((s) => (
                  <div key={s.categoria}>
                    <div className="flex justify-between text-[11px] mb-0.5">
                      <span className="text-muted-foreground truncate pr-2">{s.categoria}</span>
                      <span className="font-mono text-foreground">{s.score}</span>
                    </div>
                    <div className="h-1 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-primary"
                        style={{ width: `${Math.min(100, s.score)}%` }}
                      />
                    </div>
                  </div>
                ))}
                {compat.length === 0 && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <AlertCircle className="size-3.5" />
                    Completa tu perfil para ver scores por categoría.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <PatronCompraPanel />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Oportunidades recomendadas */}
            <div className="rounded-xl border border-border bg-card p-5 shadow-elegant">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-semibold text-foreground">Compatibilidad por categoría</h3>
                  <p className="text-xs text-muted-foreground">IA sugiere según tu perfil</p>
                </div>
                <Award className="size-5 text-primary" />
              </div>
              <div className="space-y-2">
                {compat.length ? (
                  compat.map((o) => (
                    <div
                      key={o.categoria}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/40 transition"
                    >
                      <div
                        className={cn(
                          "size-10 rounded-lg font-mono text-xs grid place-items-center font-semibold",
                          o.score >= 80
                            ? "bg-success/10 text-success"
                            : o.score >= 60
                              ? "bg-primary/10 text-primary"
                              : "bg-muted text-muted-foreground",
                        )}
                      >
                        {o.score}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{o.categoria}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {o.oportunidades} oportunidades recomendadas
                        </div>
                      </div>
                      <ArrowUpRight className="size-3.5 text-muted-foreground" />
                    </div>
                  ))
                ) : (
                  <EmptyState message="Aún no hay datos de compatibilidad. Revisa tu perfil empresa." />
                )}
              </div>
            </div>

            {/* Capacidades / Benchmark */}
            <div className="rounded-xl border border-border bg-card p-5 shadow-elegant">
              <h3 className="font-display font-semibold text-foreground mb-1">
                Benchmark vs competencia
              </h3>
              <p className="text-xs text-muted-foreground mb-4">Basado en tu historial</p>
              <div className="space-y-3">
                {benchmark.length ? (
                  benchmark.map((m) => (
                    <div key={m.metrica} className="grid grid-cols-3 items-center gap-3">
                      <div className="text-xs text-muted-foreground">{m.metrica}</div>
                      <div className="col-span-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full bg-gradient-primary rounded-full"
                            style={{
                              width: `${Math.min(
                                100,
                                m.unidad === "días"
                                  ? (m.mercado / m.empresa) * 50
                                  : (m.empresa / (m.mercado * 1.5)) * 100,
                              )}%`,
                            }}
                          />
                        </div>
                        <div className="text-xs font-mono text-foreground tabular-nums w-14 text-right">
                          {m.empresa}
                          {m.unidad}
                        </div>
                        <div className="text-xs font-mono text-muted-foreground tabular-nums w-14 text-right">
                          {m.mercado}
                          {m.unidad}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <EmptyState message="Aún no hay datos de benchmark. Revisa tu perfil empresa." />
                )}
              </div>
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border text-[11px]">
                <span className="inline-flex items-center gap-1">
                  <span className="size-2 rounded-full bg-primary" /> Tu empresa
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="size-2 rounded-full bg-muted-foreground" /> Mercado
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
      <AlertCircle className="size-8 mb-2 opacity-50" />
      <p className="text-sm max-w-xs">{message}</p>
    </div>
  );
}
